import React, { useState, useMemo } from 'react';
import '../styles/Analytics.css';

const COLORS = {
  green: '#1ab02a',
  yellow: '#ffa405',
  red: '#ef4444',
};

function getMondayOfWeek(weeksAgo = 0) {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff - weeksAgo * 7);
  return d.toISOString().slice(0, 10);
}

function getWeekKey(ta) {
  return ta.weekStartDate?.toDate
    ? ta.weekStartDate.toDate().toISOString().slice(0, 10)
    : (ta.weekStartDate?.slice?.(0, 10) ?? '');
}

function donutWedge(cx, cy, outerR, innerR, startDeg, endDeg) {
  const toRad = (deg) => (deg - 180) * (Math.PI / 180);
  const ox1 = cx + outerR * Math.cos(toRad(startDeg));
  const oy1 = cy + outerR * Math.sin(toRad(startDeg));
  const ox2 = cx + outerR * Math.cos(toRad(endDeg));
  const oy2 = cy + outerR * Math.sin(toRad(endDeg));
  const ix1 = cx + innerR * Math.cos(toRad(endDeg));
  const iy1 = cy + innerR * Math.sin(toRad(endDeg));
  const ix2 = cx + innerR * Math.cos(toRad(startDeg));
  const iy2 = cy + innerR * Math.sin(toRad(startDeg));
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${ox1} ${oy1}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${ox2} ${oy2}`,
    `L ${ix1} ${iy1}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${ix2} ${iy2}`,
    'Z',
  ].join(' ');
}

const IconChart = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const IconEmpty = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const IconLock = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const Medal1 = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ca8a04" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="14" r="7"/><path d="M8.21 3.54 6 6l2 2"/><path d="m15.79 3.54 2.21 2.46-2 2"/>
    <path d="M12 7v7"/><line x1="10" y1="11" x2="12" y2="9"/>
  </svg>
);

const Medal2 = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="14" r="7"/><path d="M8.21 3.54 6 6l2 2"/><path d="m15.79 3.54 2.21 2.46-2 2"/>
    <line x1="10" y1="17" x2="14" y2="17"/><path d="M10 13c0-1.1.9-2 2-2s2 .9 2 2-2 4-2 4"/>
  </svg>
);

const Medal3 = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="14" r="7"/><path d="M8.21 3.54 6 6l2 2"/><path d="m15.79 3.54 2.21 2.46-2 2"/>
    <path d="M10 11h4"/>
  </svg>
);

const BillingGateOverlay = () => (
  <div className="analytics-gate-overlay">
    <IconLock />
    <strong>Analytics unavailable</strong>
    <p>Your subscription limit has been reached or a payment has failed. Visit Payments &amp; Billing to restore access.</p>
  </div>
);

function SpeedDial({ value, title, type }) {
  const maxValue = 5;
  const clampedValue = Math.min(Math.max(value, 0), maxValue);
  const needleAngle = (clampedValue / maxValue) * 180 - 90;

  const getColor = () => {
    if (type === 'happiness') {
      if (clampedValue >= 3.0) return COLORS.green;
      if (clampedValue >= 1.5) return COLORS.yellow;
      return COLORS.red;
    } else {
      if (clampedValue <= 2.5) return COLORS.green;
      if (clampedValue <= 3.5) return COLORS.yellow;
      return COLORS.red;
    }
  };

  const color = getColor();
  const cx = 100, cy = 105;
  const outerR = 78, innerR = 58;

  const zones = type === 'happiness'
    ? [
        { color: COLORS.red,    start: 0,   end: 54  },
        { color: COLORS.yellow, start: 54,  end: 108 },
        { color: COLORS.green,  start: 108, end: 180 },
      ]
    : [
        { color: COLORS.green,  start: 0,   end: 90  },
        { color: COLORS.yellow, start: 90,  end: 126 },
        { color: COLORS.red,    start: 126, end: 180 },
      ];

  return (
    <div className="speed-dial">
      <div className="speed-dial-title">{title}</div>
      <div className="speed-dial-gauge">
        <svg viewBox="0 0 200 120" className="gauge-svg">
          {zones.map((z, i) => (
            <path key={i} d={donutWedge(cx, cy, outerR, innerR, z.start, z.end)} fill={z.color} opacity="0.25" />
          ))}
          <g transform={`rotate(${needleAngle}, ${cx}, ${cy})`}>
            <line x1={cx} y1={cy} x2={cx} y2={cy - innerR - 5} stroke={color} strokeWidth="3" strokeLinecap="round" />
            <circle cx={cx} cy={cy} r="7" fill={color} />
            <circle cx={cx} cy={cy} r="3.5" fill="#ffffff" />
          </g>
          <text x="16"  y="118" fontSize="11" fill="#9ca3af" fontWeight="500" textAnchor="middle">0</text>
          <text x="100" y="20"  fontSize="11" fill="#9ca3af" fontWeight="500" textAnchor="middle">2.5</text>
          <text x="184" y="118" fontSize="11" fill="#9ca3af" fontWeight="500" textAnchor="middle">5</text>
        </svg>
      </div>
      <div className="speed-dial-value-container">
        <span className="speed-dial-value" style={{ color }}>{value.toFixed(2)}</span>
        <span className="speed-dial-max">/ 5</span>
      </div>
      <div className="speed-dial-subtitle">Last 2 completed weeks</div>
    </div>
  );
}

function NewAnalyticsCharts({ teamAverages = [], teams = [], companyData, approvedEmployees = [], billingOverLimit = false }) {
  const [selectedTeam, setSelectedTeam] = useState('all');

  const twoWeekKeys = useMemo(() => [getMondayOfWeek(1), getMondayOfWeek(2)], []);
  const previousTwoWeekKeys = useMemo(() => [getMondayOfWeek(3), getMondayOfWeek(4)], []);
  const thirtyOneWeekKeys = useMemo(() => {
    const keys = [];
    for (let i = 1; i <= 31; i++) keys.push(getMondayOfWeek(i));
    return keys;
  }, []);

  const twoWeekAverages = useMemo(() => {
    const relevant = teamAverages.filter(ta => {
      const inTeam = selectedTeam === 'all' || ta.teamId === selectedTeam;
      return inTeam && twoWeekKeys.includes(getWeekKey(ta));
    });
    if (relevant.length === 0) return { happiness: 0, tiredness: 0, hasData: false };
    const total = relevant.reduce((a, b) => a + (b.surveyCount || 0), 0);
    if (total === 0) return { happiness: 0, tiredness: 0, hasData: false };
    return {
      happiness: relevant.reduce((a, b) => a + b.avgHappiness * (b.surveyCount || 0), 0) / total,
      tiredness: relevant.reduce((a, b) => a + b.avgTirednessOverall * (b.surveyCount || 0), 0) / total,
      hasData: true,
    };
  }, [teamAverages, selectedTeam, twoWeekKeys]);

  const totalResponses = useMemo(() =>
    teamAverages.filter(ta => thirtyOneWeekKeys.includes(getWeekKey(ta))).reduce((a, b) => a + (b.surveyCount || 0), 0),
    [teamAverages, thirtyOneWeekKeys]
  );

  const teamLeaderboard = useMemo(() => {
    if (!approvedEmployees || !Array.isArray(approvedEmployees)) return [];

    const currentStats = teams.map(team => {
      const memberCount = approvedEmployees.filter(emp => emp.teamId === team.id).length;
      const responseCount = teamAverages
        .filter(ta => ta.teamId === team.id && twoWeekKeys.includes(getWeekKey(ta)))
        .reduce((a, b) => a + (b.surveyCount || 0), 0);
      const responsesPerPerson = memberCount > 0 ? responseCount / memberCount : 0;
      return { id: team.id, name: team.name, responsesPerPerson, memberCount, responseCount };
    });

    const previousStats = teams.map(team => {
      const memberCount = approvedEmployees.filter(emp => emp.teamId === team.id).length;
      const responseCount = teamAverages
        .filter(ta => ta.teamId === team.id && previousTwoWeekKeys.includes(getWeekKey(ta)))
        .reduce((a, b) => a + (b.surveyCount || 0), 0);
      const responsesPerPerson = memberCount > 0 ? responseCount / memberCount : 0;
      return { id: team.id, responsesPerPerson };
    });

    const previousRanked = previousStats
      .filter(t => t.responsesPerPerson > 0)
      .sort((a, b) => b.responsesPerPerson - a.responsesPerPerson)
      .reduce((acc, team, index) => { acc[team.id] = index + 1; return acc; }, {});

    return currentStats
      .filter(t => t.responsesPerPerson > 0)
      .sort((a, b) =>
        b.responsesPerPerson !== a.responsesPerPerson
          ? b.responsesPerPerson - a.responsesPerPerson
          : b.responseCount - a.responseCount
      )
      .map((team, index) => {
        const currentRank = index + 1;
        const previousRank = previousRanked[team.id];
        return { ...team, rank: currentRank, rankChange: previousRank ? previousRank - currentRank : null };
      });
  }, [teamAverages, teams, approvedEmployees, twoWeekKeys, previousTwoWeekKeys]);

  return (
    <div className="analytics-container">
      <div className="dashboard-header">
        <div>
          <h1>Analytics</h1>
          <p>Survey data insights and trends over time, check the reports section for more detail.</p>
        </div>
      </div>

      <div className="analytics-charts-section">
        <div className="stats-row">
          <div className="stat-card stat-card-subtle">
            <div className="stat-card-icon-svg">
              <IconChart />
            </div>
            <div className="stat-card-content">
              <span className="stat-card-label">Total Responses</span>
              <span className="stat-card-value">{totalResponses.toLocaleString()}</span>
              <span className="stat-card-meta">Last 31 weeks</span>
            </div>
          </div>

          <div className="stat-card stat-card-filter">
            <select
              className="filter-select"
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
            >
              <option value="all">All Teams</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="main-chart-card" style={{ position: 'relative' }}>
          {billingOverLimit && <BillingGateOverlay />}
          <div className="chart-header">
            <h2>Average Scores</h2>
            <p className="chart-subtitle">
              {selectedTeam === 'all'
                ? 'All teams'
                : teams.find(t => t.id === selectedTeam)?.name || 'Selected team'
              } • Last 2 completed weeks
            </p>
          </div>

          {twoWeekAverages.hasData ? (
            <div className="speed-dials-grid">
              <SpeedDial value={twoWeekAverages.happiness} title="Average Happiness" type="happiness" />
              <SpeedDial value={twoWeekAverages.tiredness} title="Average Tiredness" type="tiredness" />
            </div>
          ) : (
            <div className="no-data-message">
              <span className="no-data-icon-svg"><IconEmpty /></span>
              <p>No data in the last 2 completed mon-fri weeks{selectedTeam !== 'all' && ' for this team'}</p>
            </div>
          )}
        </div>

        <div className="main-chart-card leaderboard-card" style={{ position: 'relative' }}>
          {billingOverLimit && <BillingGateOverlay />}
          <div className="chart-header">
            <h2>Team Engagement</h2>
            <p className="chart-subtitle">Ranked by responses per person (last 2 completed weeks)</p>
          </div>

          {teamLeaderboard.length > 0 ? (
            <div className="leaderboard">
              {teamLeaderboard.map((team) => (
                <div key={team.id} className={`leaderboard-item ${team.rank <= 3 ? `rank-${team.rank}` : ''}`}>
                  <div className="leaderboard-rank">
                    {team.rank === 1 && <Medal1 />}
                    {team.rank === 2 && <Medal2 />}
                    {team.rank === 3 && <Medal3 />}
                    {team.rank > 3 && <span className="rank-number">#{team.rank}</span>}
                  </div>
                  <div className="leaderboard-info">
                    <div className="team-name">{team.name}</div>
                    <div className="team-meta">
                      <span className="team-members">{team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}</span>
                      {team.rankChange !== null && team.rankChange !== 0 && (
                        <span className={`team-change ${team.rankChange > 0 ? 'positive' : 'negative'}`}>
                          {team.rankChange > 0 ? '↑' : '↓'} {Math.abs(team.rankChange)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="leaderboard-bar-container">
                    <div className="leaderboard-bar" style={{ width: `${(team.responsesPerPerson / teamLeaderboard[0].responsesPerPerson) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data-message">
              <span className="no-data-icon-svg"><IconEmpty /></span>
              <p>No team response data available in the last 2 completed weeks</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NewAnalyticsCharts;